import React from "react";

export enum StepStatus {
  COMPLETED = "completed",
  CURRENT = "current",
  PENDING = "pending",
}

export interface Step {
  id: number;
  title: string;
  status: StepStatus;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
}

export interface InitialLandingPageProps {
  onGoToChooseLoan: () => void;
}

export interface ChooseLoanPageProps {
  onLoanSelected: () => void;
}

export interface CustomerDto {
  firstName: string;
  lastName: string;
  dob?: string | null;
  email: string;
  cellPhone?: string | null;
  homePhone?: string | null;
  ssnLast4?: string | null;
}

export interface VehicleDto {
  vinLast6?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  mileage?: number | null;
}

export interface ContractDto {
  contractDate?: string | null;
  firstDueDate?: string | null;
  termMonths?: number | null;
  paymentAmount?: number | null;
  downPayment?: number | null;
}

export interface UiRequirementsDto {
  requiredDocs: string[];
  referencesRequired: number;
}

export interface Application {
  customerInfoId: string;
  dealerId: string;
  dealerName: string;
  customerName: string;
  vehicleInfo: string;
  vehicleVin: string;
  InviteId: string;
  customer: CustomerDto;
  coApplicant?: CustomerDto | null;
  vehicle?: VehicleDto | null;
  contract?: ContractDto | null;
  uiRequirements: UiRequirementsDto;
}

export interface FewQuestionsContentProps {
  onNextStep: () => void;
  mainContentClassName?: string;
  markStepAsCompleted: (stepId: number) => void;
  currentStepId: number;
}

// Internal ToggleSwitch component
export interface ToggleSwitchProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  labelYes?: string;
  labelNo?: string;
  disabled?: boolean;
  overrideLabelNoColor?: string;
  overrideLabelYesColor?: string;
}

export interface LabelledToggleSwitchProps extends ToggleSwitchProps {
  label: string;
}

export interface LabelledToggleSwitchProps extends ToggleSwitchProps {
  label: string;
}

export interface SectionHeaderProps {
  title: string;
  toggleId: string;
  toggleChecked: boolean;
  onToggleChange: (checked: boolean) => void;
}

export interface ReadOnlyFieldProps {
  label: string;
  value: string;
}

export interface InputFieldProps {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  labelColor?: string;
  maxLength?: number;
}

export interface DropdownFieldProps {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
}

export interface InsuranceUploadContentProps {
  onNextStep: () => void;
  mainContentClassName?: string;
  markStepAsCompleted: (stepId: number) => void;
  currentStepId: number;
}

export interface ShareReferencesContentProps {
  onNextStep: () => void;
  mainContentClassName?: string;
  markStepAsCompleted: (stepId: number) => void;
  currentStepId: number;
}

export interface Reference {
  name: string;
  phone: string;
  relationship: string;
  isTouched?: boolean;
}

export interface UpdateIncomeContentProps {
  onNextStep: () => void;
  mainContentClassName?: string;
  markStepAsCompleted: (stepId: number) => void;
  currentStepId: number;
  visibleSteps: Step[];
  showSubmitButton?: boolean;
}

export interface WelcomePageProps {
  userName: string;
  steps: Step[];
  onStepSelect: (stepId: number) => void;
  mainContentClassName?: string;
  stepCompletionStatus: { [key: number]: boolean };
  isLoader: boolean;
}

export interface HeaderProps {
  userName: string;
  lastName: string;
  onMenuToggle: () => void;
  isMobileMenuOpen: boolean;
  currentViewedStepId: number;
  onLogoClick: () => void; // Added new prop for logo click
  userEmail?: string;
  showMenuButton?: boolean;
}

export interface MobileBottomNavProps {
  onMenuClick: () => void;
  onNextClick: () => void;
  onBackClick: () => void;
  isBackDisabled?: boolean;
}

export interface QuickJumpNavProps {
  steps: Step[];
  currentViewedStepId: number;
  onStepSelect: (stepId: number) => void;
  isMobileBottomNavActive?: boolean;
  stepCompletionStatus: { [key: number]: boolean };
}

export interface SidebarProps {
  steps: Step[];
  currentViewedStepId: number;
  onStepSelect: (stepId: number) => void;
  onGoToAllItems: () => void;
  onCloseMenuClick: () => void;
  className?: string;
  ariaId?: string;
  isMobileMenuOpen: boolean;
  stepCompletionStatus: { [key: number]: boolean };
}

///DTO Model Interfaces
export interface AuthResponseDto {
  success: boolean;
  token: string;
  customerInfoId: string; // Guid → string
  customerName: string;
  email: string;
  dealerName: string;
  vehicleInfo?: string | null;
  expiresAt: string; // DateTime → string (ISO format)
}

export interface ApplicationStatusDto {
  customerInfoId: string;
  appNo?: number | null;
  appStatus?: string | null;
  customerName: string;
  lastName: string;
  dealerName: string;
  vehicleInfo?: string | null;
  vehicleVin?: string | null;
  completionPercentage: number;
  isComplete: boolean;
  isCoApplicant: boolean;
  references?: Reference[] | null;
  questions?: QuestionnaireDto | null;
  dealLaneQuestions: boolean;
  dealLaneReferences: boolean;
  branchNumber?: string | null;
  customer: CustomerDto;
  coApplicant?: CustomerDto | null;
  vehicle: VehicleDto;
  contract: ContractDto;
  steps: ApplicationStepsDto;
  isFromIncomeOnly?: boolean;
}

export interface ApplicationStepsDto {
  driversLicense: StepDto;
  insurance: StepDto;
  references: StepDto;
  bank: StepDto;
  payroll: StepDto;
  questionnaire: StepDto;
  residence: StepDto;
}

export interface StepDto {
  completed: boolean;
  completedAt?: string | null; // Date stored as string in API
  count?: number | null;
  method?: string | null;
  filePath?: string | null;
}

export interface QuestionnaireDto {
  isApplicantInfo?: boolean | null;
  isCoApplicantInfo?: boolean | null;
  coApplicantRelationship: string;
  isContractInfo?: boolean | null;
  vehicleColor?: string | null;
  hasPossession: boolean;
  possessionReason?: string | null;
  hasMajorMechanicalIssues: boolean;
  issueDescription?: string | null;
  dealerFixedIssues: boolean;
  isPrimaryDriver?: boolean | null;
  isPaperlessBilling?: boolean | null;
  interestInVCEasyPay?: boolean | null;
}

export interface QRProps {
  url: string;
}

export interface CanopyInsuranceConnectProps {
  publicAlias: string;
  userId: string;
  isLoader: boolean;
  isConnected: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: Error) => void;
}

export type DlScanResult = {
  success: boolean;
  fields?: Record<string, string | null>;
  error?: string;
};

export type CaptureStep = "front" | "back";
export type ScanPhase =
  | "capturing" // live camera active
  | "preview" // front captured — show thumbnail, prompt for back
  | "decoding" // PDF417 decode running (back only)
  | "decode_error" // barcode unreadable — stay on camera flow
  | "merging" // compositing front+back (near-instant)
  | "uploading"; // parent onCapture() in flight

export interface DLScanModalProps {
  open: boolean;
  onClose: () => void;
  /** Receives the final File to upload. For DL this is the merged front+back JPEG. */
  onCapture: (file: File) => Promise<void>;
  onScanned?: (data: DlScanResult) => void;
  /** Pass true only for Driver's License (currentStepId === 1). */
  isDLMode?: boolean;
}

export interface CameraCaptureProps {
  step: CaptureStep;
  phase: ScanPhase;
  frontBlob: Blob | null;
  decodeError: string | null;
  onCaptured: (blob: Blob) => void;
  onPreviewContinue: () => void;
  onRetryBack: () => void;
}
